import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { cn } from '@theprintbar/shared';
import { useState } from 'react';

export type DropdownOption = { label: string | JSX.Element; selectedLabel?: string | JSX.Element; value: any; disabled?: boolean };

interface DropdownProps<T extends DropdownOption> {
	initialValue?: T;
	buttonElement: (selectedOption: T) => JSX.Element;
	options: T[];
	zIndex?: number;
	className?: string;
	panelClassName?: string;
	buttonClassName?: string;
	optionClassName?: (active: boolean, selected: boolean) => string;
	onChange?: (value: T['value']) => void;
}

export function Dropdown<T extends DropdownOption>({
	options,
	onChange,
	initialValue,
	zIndex,
	buttonElement,
	buttonClassName,
	className,
	optionClassName,
	panelClassName,
}: DropdownProps<T>) {
	const [selectedOption, setSelectedOption] = useState(initialValue || options[0]);

	const handleChange = (option: T) => {
		setSelectedOption(option);
		onChange?.(option.value);
	};

	return (
		<Listbox value={selectedOption} onChange={handleChange}>
			<div className={cn('relative', className)}>
				<ListboxButton className={cn('w-full', buttonClassName)}>{buttonElement(selectedOption)}</ListboxButton>
				<Transition
					enter="transition duration-100 ease-out"
					enterFrom="transform scale-95 opacity-0"
					enterTo="transform scale-100 opacity-100"
					leave="transition duration-75 ease-out"
					leaveFrom="transform scale-100 opacity-100"
					leaveTo="transform scale-95 opacity-0"
				>
					<ListboxOptions
						className={cn(
							'w-full absolute shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none rounded-md bg-white',
							panelClassName,
						)}
						style={{ zIndex: zIndex || 40 }}
					>
						{options.map((option, optionKey) => (
							<ListboxOption
								disabled={option.disabled || option.value === selectedOption.value}
								key={optionKey}
								value={option}
								className={({ focus, selected }) =>
									cn(
										'relative select-none',
										optionClassName?.(focus, selected) ||
											(focus || selected ? 'bg-blue-100 text-blue' : 'text-gray-900'),
									)
								}
							>
								{option.label}
							</ListboxOption>
						))}
					</ListboxOptions>
				</Transition>
			</div>
		</Listbox>
	);
}
